/* FieldRenderers.js
    Support for rendering different field types in various contexts
*/

// Core
import React, { useEffect, useMemo, useRef, useState } from "react";

// AWS Amplify
import { DataStore } from "@aws-amplify/datastore";
import { getUrl, remove } from "@aws-amplify/storage";
import { StorageManager } from "@aws-amplify/ui-react-storage";
import { Button as AmplifyButton, Divider, Flex, Text } from "@aws-amplify/ui-react";
import AIReviewSphere, { getOutcome, getReviewType } from './AIReviewSphere';
import AiReviewCellPanel from "./AiReviewCellPanel.js";

// MUI Core Components
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  Link,
  ListItemIcon,
  MenuItem,
  Menu,
  Paper,
  Popover,
  Select,
  TextField,
  Typography,
  CircularProgress,
  Switch,
  Tooltip,
} from "@mui/material";

// MUI Date Pickers
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

// MUI Icons — default imports, one per icon
import AddCircleOutlineIcon   from "@mui/icons-material/AddCircleOutline";
import CalendarMonthIcon      from "@mui/icons-material/CalendarMonth";
import CheckIcon              from "@mui/icons-material/Check";
import CloseIcon              from "@mui/icons-material/Close";
import AccessTimeIcon         from "@mui/icons-material/AccessTime";
import Person3Icon            from "@mui/icons-material/Person3";
import PeopleAltIcon          from "@mui/icons-material/PeopleAlt";
import PersonIcon from "@mui/icons-material/Person3";
import GroupIcon from "@mui/icons-material/PeopleAlt";
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import RateReviewIcon from "@mui/icons-material/RateReview";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import { formatRelativeTime } from "../helpers/DateHelpers.js";
import { TreeView } from "@mui/lab";
import TreeItem from "@mui/lab/TreeItem";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

// Tiptap
import StarterKit from "@tiptap/starter-kit";
import Superscript from "@tiptap/extension-superscript";
import Subscript from "@tiptap/extension-subscript";
import {
  MenuButtonBold,
  MenuButtonItalic,
  MenuButtonSubscript,
  MenuButtonSuperscript,
  MenuControlsContainer,
  RichTextEditor,
} from "mui-tiptap";

// Utilities & Models
import { renderTemplates } from "./rendertemplates.js";
import { buildOptionLookup } from "./FieldOptionHelpers.js";
import FieldOptions from "./FieldOptions.js";
import { buildAiComparisonState } from "./AiFieldHelpers.js";
import AiComparisonCell from "./AiComparisonCell.js";
//import { Accounts, Forms, Projects, Submissions, Users } from "../models/index.js";
import { RenderContext } from "../Enums.js";
import { downloadAssetBlobFromAssetKey, getAssetEntryByKey } from "../asset/AssetsRegistry.js";
import DownloadIcon from "./DownloadIcon.js";

// Local Components & Styles
import "./FieldRenderers.css";

// File Icons
import csvIcon from "../assets/fileIcons/csv.svg";
import documentIcon from "../assets/fileIcons/document.svg";
import excelIcon from "../assets/fileIcons/ms-excel.svg";
import imageIcon from "../assets/fileIcons/image.svg";
import pdfIcon from "../assets/fileIcons/pdf.svg";
import wordIcon from "../assets/fileIcons/ms-word.svg";

// Date Utilities
import dayjs from "dayjs";
import "dayjs/locale/en-gb";
import customParseFormat from "dayjs/plugin/customParseFormat";


dayjs.extend(customParseFormat);

/* Halo + arrow-into-tray download icon. "small" in the grid, "large" in the detail panel. */
function FileDownloadActionButton({ title, onClick, theme, fileType, size = "small" }) {
  const fileLabel = fileType ? `${title} (${String(fileType).toUpperCase()})` : title;

  return (
    <DownloadIcon onClick={onClick} title={fileLabel} colour={theme.progressBarColour} size={size} />
  );
}

// Header renderer with tooltip, only if field.headerTooltip is set
export const makeHeaderRenderer = (field, theme) => {
  const title = field.headerName ?? field.field ?? "";
  const tooltip = field.headerTooltip;
  const showTooltipIcon = !!field?.attributes?.headerTooltipIcon;

  if (!tooltip) return undefined;

  return () => (
    <Tooltip
      arrow
      placement="top"
      title={
        <span style={{ whiteSpace: "pre-line" }}>
          {tooltip}
        </span>
      }
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: theme?.palette?.background?.paper,
            color: theme?.palette?.text?.primary,
            border: `1px solid ${theme?.palette?.divider}`,
            maxWidth: 420,
            fontSize: 12,
          },
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0, cursor: "help" }}>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {title}
        </span>

        {showTooltipIcon && (
          <HelpOutlineIcon fontSize="small" style={{ opacity: 0.75 }} />
        )}
      </Box>
    </Tooltip>
  );
};

// Shared truncating-ellipsis cell, used by aiPresentation/aiStructure/aiTheme
export const renderEllipsisCell = ({ displayValue, title }) => (
  <span className="rt-grid-ellipsis" title={title ?? displayValue}>
    {displayValue}
  </span>
);

export const renderAiSuggestedBadge = ({ displayValue, isAmbiguous, candidateCount, tone }) => {
  if (!displayValue) return null;

  return (
    <div
      title={displayValue}
      style={{
        width: "100%",
        display: "flex",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          maxWidth: "100%",
          minWidth: 0,
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "4px 9px",
          borderRadius: 999,
          border: `1px solid color-mix(in srgb, ${tone} 38%, transparent)`,
          background: `linear-gradient(180deg, color-mix(in srgb, ${tone} 16%, transparent) 0%, color-mix(in srgb, ${tone} 8%, transparent) 100%)`,
          boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
        }}
      >
        <span
          style={{
            width: 16,
            height: 16,
            borderRadius: "50%",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            color: tone,
            background: `color-mix(in srgb, ${tone} 12%, transparent)`,
            flexShrink: 0,
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: 10 }} />
        </span>
        <span
          className="rt-grid-ellipsis"
          style={{
            minWidth: 0,
            fontSize: 11,
            fontWeight: 700,
            color: tone,
            letterSpacing: "0.01em",
          }}
        >
          {displayValue}
        </span>
        {isAmbiguous && candidateCount > 1 ? (
          <span
            style={{
              flexShrink: 0,
              fontSize: 10,
              fontWeight: 800,
              color: tone,
            }}
          >
            {candidateCount}
          </span>
        ) : null}
      </div>
    </div>
  );
};

const describeChange = ({ field, prevValue, nextValue }, fieldLabelsByKey = {}) => {
  const label = fieldLabelsByKey[field] || field;

  // Same option list the cell renderer uses (FieldOptions, a warm synchronous
  // cache) — shows "Systematic Review", not the raw stored "SYSTEMATIC_REVIEW".
  const optionLookup = buildOptionLookup({
    flatOptions: FieldOptions.resolveFlatOptions({ fieldName: field }) || [],
    treeOptions: FieldOptions.resolveTreeOptions({ fieldName: field }) || [],
  });
  const toDisplay = (v) => optionLookup.labelForValue(v) ?? String(v);

  if (Array.isArray(prevValue) && Array.isArray(nextValue)) {
    const isPrimitive = (v) => v === null || typeof v !== "object";
    if (prevValue.every(isPrimitive) && nextValue.every(isPrimitive)) {
      const added = nextValue.filter((v) => !prevValue.includes(v));
      const removed = prevValue.filter((v) => !nextValue.includes(v));
      const parts = [];
      if (added.length > 0) parts.push(`added ${added.map(toDisplay).join(", ")}`);
      if (removed.length > 0) parts.push(`removed ${removed.map(toDisplay).join(", ")}`);
      return { label, kind: "note", text: parts.join("; ") || "no change" };
    }
    // array of objects (e.g. authors) — no readable per-item text
    return { label, kind: "note", text: "updated" };
  }

  const now = nextValue === null || nextValue === undefined || nextValue === "" ? "—" : toDisplay(nextValue);
  const was = prevValue === null || prevValue === undefined || prevValue === "" ? null : toDisplay(prevValue);
  return { label, kind: "transition", was, now };
};

// Bell + click-to-view feed: outline empty, pulsing while flashing, solid+badge once persisted
export const NotificationBell = ({ value, persistedNotifications = [], fieldLabelsByKey = {}, theme, onDismiss, trackingNumber, entityTitle }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const isFlashing = Array.isArray(value) ? value.length > 0 : !!value;
  const persistedCount = persistedNotifications.length;

  // describeChange resolves option labels per change — only worth redoing when the
  // notifications themselves change, not on every unrelated re-render of this cell.
  const feedItems = useMemo(
    () =>
      persistedNotifications.slice().reverse().map((notification) => (
        <Paper key={notification.id} elevation={0} className="fr-notifications-feed-item">
          {(notification.changes || []).map((change, i) => {
            const desc = describeChange(change, fieldLabelsByKey);
            return (
              <Box key={i} className="fr-notifications-change-row">
                <Box className="fr-notifications-change-row-top">
                  <Typography className="fr-notifications-change-field">
                    {desc.label}
                  </Typography>
                  <Typography variant="caption" className="fr-notifications-feed-time">
                    {formatRelativeTime(notification.createdAt)}
                  </Typography>
                </Box>
                {desc.kind === "transition" ? (
                  <Typography className="fr-notifications-transition">
                    {desc.was && (
                      <>
                        <span className="fr-notifications-was">{desc.was}</span>
                        <span className="fr-notifications-arrow">→</span>
                      </>
                    )}
                    <span className="fr-notifications-now">{desc.now}</span>
                  </Typography>
                ) : (
                  <Typography className="fr-notifications-updated">
                    {desc.text}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Paper>
      )),
    [persistedNotifications, fieldLabelsByKey]
  );

  const handleOpen = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    onDismiss?.();
  };

  let icon;
  if (isFlashing) {
    icon = <NotificationsActiveIcon fontSize="medium" className="fr-notifications-bell-active" />;
  } else if (persistedCount > 0) {
    icon = (
      <Badge badgeContent={persistedCount} overlap="circular" className="fr-notifications-badge">
        <NotificationsActiveIcon fontSize="medium" className="fr-notifications-bell-persisted" />
      </Badge>
    );
  } else {
    icon = <NotificationsNoneIcon fontSize="medium" className="fr-notifications-bell-empty" />;
  }

  return (
    <>
      <span
        onClick={persistedCount > 0 ? handleOpen : undefined}
        className={persistedCount > 0 ? "fr-notifications-bell-clickable" : ""}
      >
        {icon}
      </span>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{ className: "fr-notifications-feed-paper" }}
      >
        <Box className="fr-notifications-feed">
          <Box className="fr-notifications-feed-header">
            <Box className="fr-notifications-feed-header-text">
              <Box className="fr-notifications-feed-header-top">
                <Typography variant="subtitle2">Notifications</Typography>
                {trackingNumber ? (
                  <Typography variant="caption" className="fr-notifications-feed-tracking">
                    #{trackingNumber}
                  </Typography>
                ) : null}
              </Box>
              {entityTitle ? (
                <Tooltip title={entityTitle} arrow enterDelay={400}>
                  <Typography className="fr-notifications-entity-title">
                    {entityTitle}
                  </Typography>
                </Tooltip>
              ) : null}
            </Box>
            <IconButton size="small" onClick={handleClose} className="fr-notifications-feed-close">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          {feedItems}
        </Box>
      </Popover>
    </>
  );
};

export const renderDateCell = ({ value, onChange, editable }) => {
    const dateValue =
      typeof value === "object" && value !== null
        ? value["$d"]
        : value;
    const initialValue = dateValue && dayjs(dateValue).isValid() ? dayjs(dateValue) : null;
  if (editable) {
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DesktopDatePicker
          inputFormat="DD/MM/YYYY"
          value={initialValue}
          onChange={onChange}
          components={{
            textField: ({ inputRef, inputProps, InputProps }) => (
              <TextField
                {...inputProps}
                ref={inputRef}
                InputProps={{ ...InputProps }}
              />
            ),
          }}
        />
      </LocalizationProvider>
    );
  }
  else{
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DesktopDatePicker
          inputFormat="DD/MM/YYYY"
          value={initialValue}
          components={{
            textField: ({ inputRef, inputProps, InputProps }) => (
              <TextField
                {...inputProps}
                ref={inputRef}
                InputProps={{ ...InputProps }}
              />
            ),
          }}
        />
      </LocalizationProvider>
    );
  }
};

export const renderDateTimeCell = ({ value, onChange, editable }) => {
  const dateValue =
    typeof value === "object" && value !== null
      ? value["$d"]
      : value;

  const initialValue =
    dateValue && dayjs(dateValue).isValid() ? dayjs(dateValue) : null;

  if (editable) {
    return (
      <TextField
        value={initialValue ? initialValue.format("DD/MM/YYYY HH:mm") : ""}
        onChange={onChange}
        variant="outlined"
        size="small"
        fullWidth
      />
    );
  }

  if (!initialValue) return "";

  const datePart = initialValue.format("DD/MM/YYYY");
  const timePart = initialValue.format("HH:mm");
  const title = initialValue.format("DD/MM/YYYY HH:mm");

  return (
    <Box
      title={title}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        minWidth: 0,
        whiteSpace: "nowrap",
      }}
    >
      <CalendarMonthIcon sx={{ fontSize: 16, color: "text.secondary", flexShrink: 0 }} />
      <Typography
        component="span"
        variant="body2"
        sx={{ color: "text.primary", flexShrink: 0 }}
      >
        {datePart}
      </Typography>
      <AccessTimeIcon sx={{ fontSize: 14, color: "text.secondary", flexShrink: 0 }} />
      <Typography
        component="span"
        variant="body2"
        sx={{ color: "text.secondary", flexShrink: 0 }}
      >
        {timePart}
      </Typography>
    </Box>
  );
};

const renderMailtoLink = (email) =>
  email
    ? (
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
        <EmailOutlinedIcon sx={{ fontSize: 15, color: "text.secondary", flexShrink: 0 }} />
        <Link
          href={`mailto:${email}`}
          underline="hover"
          color="inherit"
          onClick={(e) => e.stopPropagation()}
          sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        >
          {email}
        </Link>
      </Box>
    )
    : '';

// key naming: "first_name"/"last_name"/"email" read the row's own owner (meta.owner);
// "app_first_name"/"app_last_name"/"app_email" read the linked/underlying submission's
// owner (submission.meta.owner) — the "app_" prefix exists only because a DataGrid column's
// `field` must be unique, so a review row showing both owners needs two distinct keys per
// name/email. Both prefixes read the same ownerData shape (givenName/familyName/email/userId).
export const getDataFromOwner = (owner, key) => {
  //console.log("1) owner", owner);
  //console.log("2) key", key);

  if (!owner || !key) {
    return '';
  }
    // Parse the JSON string
    let ownerData;
    if (typeof owner === 'string') {
      // Parse the JSON string
      try {
        ownerData = JSON.parse(owner);
      } catch (error) {
        console.error('Invalid JSON string:', error);
        return '';
      }
    } else if (typeof owner === 'object') {
      // Already a JSON object
      ownerData = owner;
    } else {
      console.error('Invalid owner type');
      return '';
    }

    switch (key) {
      case 'first_name':
      case 'app_first_name':
        return ownerData.givenName;
      case 'last_name':
      case 'app_last_name':
        return ownerData.familyName;
      case 'user_id':
        return ownerData.userId;
      case 'email':
      case 'app_email':
        return renderMailtoLink(ownerData.email);
      default:
        const value = ownerData[key] || '';
        return value;
    }
  };

export const handleSelectChange = async (rowId, newValue, field, onCellUpdate) => {
    console.log("Select change:", rowId, newValue, field, onCellUpdate);
  if (onCellUpdate) {
    console.log("Select change2:", onCellUpdate);
    onCellUpdate(rowId, newValue, field);
  }
};

export const handleFileChange = (rowId, newValue, dataKey, onFileUpdate) => {
  if (onFileUpdate) {
    console.log("File change detected. Calling onFileUpdate with params:", {
      rowId,
      newValue,
      dataKey
    });
    // Call onFileUpdate directly with the correct params
    onFileUpdate(rowId, newValue, dataKey);
  }
};

export const handleReviewerChange = async (rowId, newValue, onReviewerChange) => {
  console.log("im in handleReviewerChange");
  console.log("RowId: ", rowId);
  console.log("NewValue: ", newValue);

  //N.B. DC - This is to avoid hardcoded a case where the reviewer bool is changed
  //as it is not part of the user datastore - it is injected from project.meta.users.tokens
  //in which case it needs to go back into projects object.
  //This relies heavily on the view.column.fieldType for reviewer being set to "reviewer"
  if (onReviewerChange) {
    onReviewerChange(rowId, newValue);
  }
return;
/*
  try {
    // Step 1: Query the project based on the projectToken
    const projects = await DataStore.query(Projects, c => c.token.eq(projectToken));
    console.log("projects: ", projects);
    const project = projects.length > 0 ? projects[0] : null;

    if (!project) {
      console.error("Project not found for the given token:", projectToken);
      return;
    }

    // Step 2: Find the user within the project.meta.users array
    const userIndex = project.meta.users.findIndex(user => user.id === rowId);
    if (userIndex === -1) {
      console.error("User not found in project.meta.users with rowId:", rowId);
      return;
    }

    // Step 3: Modify the tokens array based on newValue
    const updatedUsers = [...project.meta.users];
    const userTokens = new Set(updatedUsers[userIndex].tokens);
   
    if (newValue === "Yes") {
      userTokens.add(reviewingToken);
    } else if (newValue === "No") {
      userTokens.delete(reviewingToken);
    } else {
      console.error("Invalid newValue:", newValue);
      return;
    }
    //console.log("userTokens: ", userTokens);

    updatedUsers[userIndex] = {
      ...updatedUsers[userIndex],
      tokens: Array.from(userTokens),
    };
    //console.log("updatedUsers: ", updatedUsers);

    // Step 4: Update the project with the modified users array
    await DataStore.save(
      Projects.copyOf(project, updated => {
        updated.meta.users = updatedUsers;
      })
    );

    //console.log("Reviewer status updated successfully.");
  } catch (error) {
    console.error("Error updating reviewer status:", error);
  }

  //console.log("Project updated successfully");
*/
};

// Function to render select
export const renderReviewingType = ({ value = [], onChange, editable, options = [] }) => {
  //console.log("value: ", value);
  //console.log("options: ", options);

  // Step 1: Build a set of all unique tokens across options for relevance checking
  const relevantTokens = new Set();
  options.forEach(option => {
    option.value.split(',').forEach(token => relevantTokens.add(token));
  });

  // Step 2: Filter `value` to only include relevant tokens
  const filteredValue = value.filter(token => relevantTokens.has(token));

  // Step 3: Find the first matching option where all tokens are included
  const matchingOption = options.find(option => {
    const requiredTokens = new Set(option.value.split(','));
    return [...requiredTokens].every(token => filteredValue.includes(token));
  });

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
      }}
    >
      {matchingOption?.name || ''}
    </Box>
  );
};

export const downloadFile = async (fileKey, fileName) => {
  try {
    console.groupCollapsed(`downloadFile start: ${fileName}`);
    console.log('■ raw fileKey:', fileKey);
    console.log('■ raw fileName:', fileName);

    // Check if fileKey already has "public/" to avoid double-prefix
    if (fileKey.startsWith('public/')) {
      console.warn('⚠️ fileKey already contains "public/":', fileKey);
    }

    // Build the full S3 key (exactly what will be signed)
    const fullKey = fileKey.startsWith('public/')
      ? fileKey
      : `public/${fileKey}`;
    console.log('✔️ fullKey to sign:', fullKey);

    // Generate the signed URL
    const { url } = await getUrl({ path: fullKey, level: 'public' });
    console.log('✔️ signed URL:', url);

    // Parse it for clarity
    try {
      const parsed = new URL(url);
      console.log('   parsed.origin:', parsed.origin);
      console.log('   parsed.pathname:', parsed.pathname);
      console.log('   parsed.search:', parsed.search);
    } catch (e) {
      console.warn('⚠️ could not parse signed URL:', e);
    }

    // Fetch the object
    const response = await fetch(url);
    console.log('✔️ fetch response status:', response.status);

    if (!response.ok) {
      throw new Error(`Fetch failed: ${response.status} ${response.statusText}`);
    }

    const blob = await response.blob();
    console.log('✔️ blob size:', blob.size, 'type:', blob.type);

    // Trigger download
    const downloadUrl = URL.createObjectURL(blob);
    console.log('✔️ downloadUrl created');

    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();

    URL.revokeObjectURL(downloadUrl);
    console.log('✔️ revoked object URL');

    console.groupEnd();
  } catch (err) {
    console.groupCollapsed(`downloadFile ERROR: ${fileName}`);
    console.error(err);
    console.groupEnd();
  }
};

function saveBlobAsFile(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName || "download";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 250);
}

export function renderAssetFileDownloadButton({ linkedData, rowId, field, theme, context }) {
  if (!Array.isArray(linkedData) || !field) return null;

  const template = field.template;
  if (!template) return null;

  const linked = linkedData.find(
    (x) =>
      x &&
      x.template === template &&
      typeof x.key === "string" &&
      x.key.length > 0
  );

  if (!linked) return null;

  const assetKey = linked.key;
  const fileType = linked.fileType || "FILE";
  const fileName =
    linked.fileType === "JSON"
      ? `${rowId || assetKey}.json`
      : `${rowId || assetKey}`;

  return (
    <Box className="file-download-cell" sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%" }}>
      <FileDownloadActionButton
        title="Download file"
        theme={theme}
        fileType={fileType}
        size={context === RenderContext.DETAIL_PANEL ? "large" : "small"}
        onClick={async () => {
          const blob = await downloadAssetBlobFromAssetKey(assetKey);
          saveBlobAsFile(blob, fileName);
        }}
      />
    </Box>
  );
}

// updated to use the new format
export const renderLanguageSelect = ({ value, onChange, options, context }) => {
  let countryList;

  countryList = options.map(({ name, value }) => ({
    name,
    value
  }));

  // Ensure the value matches one of the provided options or set it to an empty string
  const isValidValue = countryList.some(
    (option) => option.value === value || option.name === value
  );

  let safeOption = {};
  if (isValidValue) {
    safeOption = countryList.find(
      (option) => option.value === value || option.name === value
    );
  }

  const safeLabel = isValidValue ? safeOption.name : (value || '');
  const safeValue = isValidValue ? safeOption.value : (value || '');

  const contextClass =
    context === RenderContext.META_PANEL ? `render-language-${context.toLowerCase()}` : "";

  return (
    <Select
      value={safeValue}
      onChange={onChange}
      displayEmpty
      inputProps={{ "aria-label": "Language select" }}
      size="small"
      fullWidth
      className={`language-select ${contextClass}`}
    >
      <MenuItem value="">
        {/* Blank option for empty/default */}
      </MenuItem>
      {countryList.map(({ name, value }) => (
        <MenuItem value={value} key={name} className="language-option">
          {value === "en" ? (
            <img
              src={`https://flagcdn.com/w20/gb-eng.png`} // Use gb-eng flag for English language
              alt={`${name} flag`}
              className="language-flag"
            />
          ) : (
            value && (
              <img
                src={`https://flagcdn.com/w20/${value.toLowerCase()}.png`}
                alt={`${name} flag`}
                className="language-flag"
              />
            )
          )}
          {name}
        </MenuItem>
      ))}
    </Select>
  );
};

export const StatusSelect = ({ value, onChange }) => (
  <Select
    value={value || ''}
    onChange={onChange}
    displayEmpty
    inputProps={{ "aria-label": "Without label" }}
    size="small"
    fullWidth
    sx={{
      border: "none",
      "& .MuiOutlinedInput-notchedOutline": {
        border: "none",
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        border: "none",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        border: "none",
        boxShadow: "none",
      },
      // Ensuring text aligns left in the input field itself
      ".MuiSelect-select": {
        textAlign: "left",
      },
    }}
  >
    <MenuItem value=" "></MenuItem>
    <MenuItem
      value="Accepted"
      sx={{ display: "flex", alignItems: "center", textAlign: "left" }}
    >
      Accepted
    </MenuItem>
    <MenuItem
      value="Rejected"
      sx={{ display: "flex", alignItems: "center", textAlign: "left" }}
    >
      Rejected
    </MenuItem>
    <MenuItem
      value="Withdrawn"
      sx={{ display: "flex", alignItems: "center", textAlign: "left" }}
    >
      Withdrawn
    </MenuItem>
  </Select>
);

export const BooleanSelect = ({ value, onChange }) => (
  <Select
    value={value || ''}
    onChange={onChange}
    displayEmpty
    inputProps={{ "aria-label": "Without label" }}
    size="small"
    fullWidth
    sx={{
      border: "none",
      "& .MuiOutlinedInput-notchedOutline": {
        border: "none",
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        border: "none",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        border: "none",
        boxShadow: "none",
      },
      // Ensuring text aligns left in the input field itself
      ".MuiSelect-select": {
        textAlign: "left",
      },
    }}
  >
  <MenuItem value="">
    {/* Display this when no option is selected */}
    {/* Blank option */}
  </MenuItem>
  <MenuItem
    value="Yes" // Changed to boolean true
    sx={{ display: 'flex', alignItems: 'center', textAlign: 'left' }}
  >
    <ListItemIcon>
      <CheckIcon />
    </ListItemIcon>
  </MenuItem>
  <MenuItem
    value="No" // Changed to boolean false
    sx={{ display: 'flex', alignItems: 'center', textAlign: 'left' }}
  >
    <ListItemIcon>
      <CloseIcon />
    </ListItemIcon>
  </MenuItem>
</Select>
);

export const YesNoDisplay = ({ value }) => {
  if (value === "Yes") {
    return <CheckIcon />;
  }
  if (value === "yes") {
    return <CheckIcon />;
  }
  if (value === true) {
    return <CheckIcon />;
  }
  if (value === "No") {
    return <CloseIcon />;
  }
  if (value === "no") {
    return <CloseIcon />;
  }
  if (value === false) {
    return <CloseIcon />;
  }
};

// New renderProgressBar function
export const renderProgressBar = (value, theme) => {
  const raw = value ?? 0;
  const pct = Math.max(0, Math.min(100, Number(raw) || 0));

  const bgcolor = theme.progressBarColour;

  const containerStyles = {
    height: "20px",
    width: "100%",
    backgroundColor: "#e0e0de",
    borderRadius: "50px",
    margin: "5px",
  };

  const fillerStyles = {
    height: "100%",
    width: `${pct}%`,
    backgroundColor: bgcolor,
    borderRadius: "inherit",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    transition: "width 1s ease-in-out",
  };

  const labelStyles = {
    padding: 5,
    color: "white",
    fontWeight: "bold",
  };

  return (
    <div style={containerStyles}>
      <div style={fillerStyles}>
        <span style={labelStyles}>{`${pct}%`}</span>
      </div>
    </div>
  );
};


// Function to render Yes/No display
export const render2StatePositiveBoolean = ({ value, onChange, editable }) => {
  const val = formatValue( value );
    if(editable) {
        return (
          <Select
            value={val || ''}
            onChange={onChange}
            displayEmpty
            inputProps={{ "aria-label": "Without label" }}
            size="small"
            fullWidth
            sx={{
              border: "none",
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                border: "none",
                boxShadow: "none",
              },
               // Ensuring text aligns left in the input field itself
              ".MuiSelect-select": {
                textAlign: "left",
              },
            }}
          >
          <MenuItem value="">
            {/* Display this when no option is selected */}
            {/* Blank option */}
          </MenuItem>
          <MenuItem
            value="Yes" // Changed to boolean true
            sx={{ display: 'flex', alignItems: 'center', textAlign: 'left' }}
          >
            <ListItemIcon>
              <CheckIcon />
            </ListItemIcon>
          </MenuItem>
          <MenuItem
            value="No" // Changed to boolean false
            sx={{ display: 'flex', alignItems: 'center', textAlign: 'left' }}
          >
            <ListItemIcon>
              <CloseIcon />
            </ListItemIcon>
          </MenuItem>
        </Select>
      );
    } else {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'left',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <YesNoDisplay value={val} style={{ margin: '0 auto', display: 'block' }} />
        </Box>
        )
      }
};

// Per-role badge config for renderUserRole, keyed by field.attributes.userRole.
// Colours read from theme.dataTableColours, not the flat submissionColour/
// reviewColour keys used elsewhere (e.g. the Dynamic Island's dark panels) — those
// were tuned for a dark, translucent surface and read poorly on the grid's white
// rows. dataTableColours holds values chosen for that white background instead.
const USER_ROLE_BADGES = {
  submitter: { Icon: PersonIcon, label: "Submitter", getColour: (theme) => theme?.dataTableColours?.submissionColour },
  reviewer: { Icon: RateReviewIcon, label: "Reviewer", getColour: (theme) => theme?.dataTableColours?.reviewColour },
  admin: { Icon: AdminPanelSettingsIcon, label: "Admin", getColour: (theme) => theme?.dataTableColours?.adminColour },
};

/**
 * renderUserRole — generic per-role badge (submitter/reviewer/admin/…), one
 * column per role so each sorts independently on field.dataSource. Which role a
 * column represents comes from field.attributes.userRole (presentation only);
 * dataSource still resolves the boolean value, same as every other field type.
 * Icon colour reads the role's theme key (see USER_ROLE_BADGES); when unset,
 * `color` is left undefined so the icon falls through to currentColor — the
 * row's own text colour — rather than a hardcoded fallback hiding a missing key.
 * Fields.js currently wires the same onChange (handleReviewerChange) into every
 * userRole column; it only takes effect where editable: true (today, only
 * reviewer's). A role that needs its own click behaviour should get its own
 * callback wired in Fields.js — extend field.attributes to name which handler
 * to use, rather than growing this one to branch on roleKey.
 */
export const renderUserRole = ({ value, field, theme, onChange, editable }) => {
  const roleKey = field?.attributes?.userRole;
  const badge = USER_ROLE_BADGES[roleKey];
  const colour = badge?.getColour(theme);
  const val = formatValue(value);

  if (editable) {
    return (
      <Select
        value={val || ""}
        onChange={onChange}
        displayEmpty
        // Explicit renderValue: without it, Select clones the matching MenuItem's
        // children into the closed box, which overflows an icon-only item in a
        // narrow column and shows a stray text-overflow ellipsis. This also lets
        // "No" collapse to blank instead of a cross.
        renderValue={(selected) => {
          if (selected !== "Yes") return null;
          const Icon = badge ? badge.Icon : CheckIcon;
          return badge ? (
            <Tooltip title={badge.label}>
              <Icon fontSize="small" style={{ color: colour }} />
            </Tooltip>
          ) : (
            <Icon fontSize="small" />
          );
        }}
        inputProps={{ "aria-label": "Without label" }}
        size="small"
        fullWidth
        MenuProps={{ disableScrollLock: true }}
        sx={{
          border: "none",
          "& .MuiOutlinedInput-notchedOutline": { border: "none" },
          "&:hover .MuiOutlinedInput-notchedOutline": { border: "none" },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            border: "none",
            boxShadow: "none",
          },
          // Select always reserves space on the right for its own dropdown arrow
          // (MuiSelect-icon) — hiding the arrow alone leaves that padding behind,
          // so the badge stays off-centre in a narrow column unless both are cleared.
          "& .MuiSelect-icon": { display: "none" },
          ".MuiSelect-select": {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            paddingRight: "8px !important",
          },
        }}
      >
        <MenuItem value="">{/* Blank option */}</MenuItem>

        <MenuItem value="Yes" sx={{ display: "flex", alignItems: "center", textAlign: "left" }}>
          <ListItemIcon>{badge ? <badge.Icon style={{ color: colour }} /> : <CheckIcon />}</ListItemIcon>
        </MenuItem>

        <MenuItem value="No" sx={{ display: "flex", alignItems: "center", textAlign: "left" }}>
          <ListItemIcon>
            <CloseIcon />
          </ListItemIcon>
        </MenuItem>
      </Select>
    );
  }

  return (
    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
      {value && badge ? (
        <Tooltip title={badge.label}>
          <badge.Icon fontSize="small" style={{ color: colour }} />
        </Tooltip>
      ) : null}
    </Box>
  );
};


/**
 * renderSelect(req)
 * ---------------------------------------------------------------------------
 * Orchestrator for select-type field rendering. Normalizes data value -> UI value,
 * resolves the renderer by field.renderType, and passes a consistent prop shape.
 *
 * Renderer resolution:
 *  - Uses field.renderType when provided.
 *  - If field.attributes.multiple === true and renderType is "DefaultSelect",
 *    routes to "MultiSelect" automatically.
 *
 * META behavior:
 *  - If env.context === RenderContext.META_PANEL, returns plain textual output
 *    (no styling, no MUI components).
 *
 * Normalization:
 *  - Multiple fields => [] (never null/undefined).
 *  - Single fields => "" for UI when null/undefined (MUI Select cannot take null).
 *  - On clear for single-select: passes null to data if allowNull, else "".
 *  - Multi-select values are passed through as arrays.
 *
 * Options:
 *  - Flat options may be supplied either as a plain array [{ name, value }]
 *    or as options.flatOptions.
 *  - Tree options are supplied as options.treeOptions.
 *
 * Branding:
 *  - theme is passed through unchanged (incl. theme.selectTokens).
 */
export const renderSelect = (req) => {
  const { field, value, env, cb, options } = req;

  const multiple  = !!field?.attributes?.multiple;
  const editable  = !!field?.editable;
  const allowNull = field?.allowNull !== false; // default true

  const requestedRenderType = field?.renderType || "DefaultSelect";
  const resolvedRenderType =
    multiple && requestedRenderType === "DefaultSelect"
      ? "MultiSelect"
      : requestedRenderType;

  const renderer = renderTemplates[resolvedRenderType] || renderTemplates.DefaultSelect;

  const normalizeSelectValue = (val) => {
    if (multiple) {
      return Array.isArray(val) ? val : [];
    }
    if (val === null || val === undefined) return "";
    return val;
  };

  const normalizedValueRaw = normalizeSelectValue(value);

  const normalizedValueForSelect = multiple
    ? normalizedValueRaw
    : (normalizedValueRaw === null || normalizedValueRaw === undefined ? "" : normalizedValueRaw);

  const safeFlat = Array.isArray(options)
    ? options
    : Array.isArray(options?.flatOptions)
      ? options.flatOptions
      : [];

  const safeTree = Array.isArray(options?.treeOptions) ? options.treeOptions : [];

  const optionLookup = buildOptionLookup({
    flatOptions: safeFlat,
    treeOptions: safeTree,
  });
  const optionValues = optionLookup.values;
  const valueLabel = optionLookup.labelForValue;

  const warnKeyForValue = (val) =>
    `${field?.field ?? "unknown"}|${field?.dataSource ?? "unknown"}|${String(val)}`;

  if (!renderSelect.invalidOptionWarnings) {
    renderSelect.invalidOptionWarnings = new Set();
  }

  const warnUnknownValue = (val) => {
    if (val === "" || val === null || val === undefined) return;
    if (optionValues.has(String(val))) return;

    const warnKey = warnKeyForValue(val);
    if (renderSelect.invalidOptionWarnings.has(warnKey)) return;

    renderSelect.invalidOptionWarnings.add(warnKey);

    const optionsSource = getAssetEntryByKey(field?.field)
      ? "asset"
      : field?.options !== undefined
        ? "inline"
        : "none configured";

    console.warn(
      `[renderSelect] Current value is not present in the configured options for "${field?.field}".`,
      {
        dataSource: field?.dataSource,
        value: val,
        optionsSource,
        knownOptions: [...optionValues],
      }
    );
  };

  if (multiple) {
    const vals = Array.isArray(value) ? value : [];
    vals.forEach(warnUnknownValue);
  } else {
    warnUnknownValue(value);
  }

  const makePlainText = () => {
    if (multiple) {
      const vals = Array.isArray(value) ? value : [];
      const labels = vals.map((v) => valueLabel(v) ?? v).filter(Boolean);
      return labels.length ? labels.join("; ") : "";
    }
    return valueLabel(value) ?? (value ?? "");
  };

  const ctx = env?.context;
  const variant =
    ctx === RenderContext.DATAGRID ? "grid" :
    ctx === RenderContext.META_PANEL ? "meta" :
    "detail";

  if (ctx === RenderContext.META_PANEL) {
    return makePlainText();
  }

  let uiValue;
  if (multiple) {
    const arr = Array.isArray(normalizedValueForSelect)
      ? normalizedValueForSelect
      : [];
    uiValue = arr.filter((v) => optionValues.has(String(v)));
  } else {
    const candidate = normalizedValueForSelect;
    const hasOptionForValue =
      candidate !== "" &&
      candidate !== null &&
      candidate !== undefined &&
      optionValues.has(String(candidate));
    uiValue = hasOptionForValue ? candidate : "";
  }

  const aiComparisonState =
    field?.attributes?.aiComparison?.enabled === true
      ? buildAiComparisonState({
          row: req.row,
          field,
          submitterValue: value,
          targetOptions: {
            flatOptions: safeFlat,
            treeOptions: safeTree,
          },
          projectConfig: env?.projectConfig,
        })
      : null;

  if (field?.attributes?.aiComparison?.debug === true && aiComparisonState) {
    console.log("[renderSelect] AI comparison state", {
      rowId: req?.row?.id,
      field: field?.field,
      dataSource: field?.dataSource,
      aiComparisonState,
    });
  }

  const onChange = (eOrValue) => {
    const next = eOrValue && eOrValue.target !== undefined ? eOrValue.target.value : eOrValue;

    let out = next;

    if (!multiple) {
      if (out === "" || out === undefined) {
        out = allowNull ? null : "";
      }
    }

    cb?.onCellUpdate?.(req.row.id, out, field.dataSource);
  };

  const rendered = renderer({
    normalizedValue: uiValue,
    multiple,
    editable,
    value,
    onChange,
    options: safeFlat,
    tree: safeTree,
    theme: env?.theme,
    allowNull,
    context: ctx,
    env,
    variant,
    aiComparisonState,
  });

  const shouldRenderAiComparisonCell =
    ctx === RenderContext.DATAGRID &&
    aiComparisonState?.enabled &&
    aiComparisonState?.display?.cell !== false &&
    aiComparisonState?.display?.cell !== "none";

  if (shouldRenderAiComparisonCell) {
    const handleOpenAiComparisonPanel = (event) => {
      req.cb?.onOpenAiReviewPanel?.({
        row: req.row,
        value: {
          comparison: aiComparisonState,
          submitterValue: value,
          aiValue: aiComparisonState?.aiValue,
          aiLabel: aiComparisonState?.aiLabel,
          aiCandidates: aiComparisonState?.aiCandidates,
        },
        fieldConfig: {
          ...field,
          aiComparisonState,
        },
        anchorEl: event?.currentTarget || null,
      });
    };

    return (
      <AiComparisonCell
        comparison={aiComparisonState}
        theme={env?.theme}
        onOpenComparison={handleOpenAiComparisonPanel}
      >
        {rendered}
      </AiComparisonCell>
    );
  }

  return rendered;
};

// Clears stale value on render if row is no longer a reviewer — may refire until the write lands
export const renderReviewerSpecialistAreas = (req) => {
  const { field, value, row } = req;
  const isReviewer = !!row?.reviewer;

  if (!isReviewer) {
    if (value != null) {
      req.cb.onReviewerChange?.(row.id, null, field.field);
    }
    return null;
  }

  const req2 = {
    ...req,
    cb: {
      ...req.cb,
      onCellUpdate: (userId, out) => {
        req.cb.onReviewerChange?.(userId, out, field.field);
      },
    },
  };

  return renderSelect(req2);
};




// Helper function to check if the file is uploaded
export const isFileUploaded = (files, dataKey) => {
  if (!files) {
    return "No";
  }
  const fileData = files.find(element => element.dataKey === dataKey);
  return fileData ? "Yes" : "No";
};
  
// Helper function to render the file cell
export const renderFileCell = (params, dataKey, projectCode, theme) => {
  try {
    const obj = params.files;
    if (!obj) {
      return "";
    }
    const dat = obj.find(element => element.dataKey === dataKey);
    if (!dat) {
      return null;
    }

    const extension = dat.fileKey.split(".").pop();
    const trackingNumber = params.meta.trackingNumber;
    const fileName = trackingNumber
    ? `${projectCode}_${trackingNumber}_${dataKey}.${extension}`
    : `${projectCode}_${dataKey}.${extension}`;

    return (
      <Box className="file-download-cell" sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%" }}>
        <FileDownloadActionButton title="Download file" theme={theme} fileType={extension} onClick={() => downloadFile(dat.fileKey, fileName)} />
      </Box>
    );
  } catch (error) {
    console.error(error);
    return null;
  }
};

/**
 * Renders a “Download JSON” button for the generated submission JSON.
 *
 * @param {Object} params        – submission record; must have .id (UUID)
 * @param {string} accountToken  – folder in S3
 * @param {string} projectToken  – subfolder in S3
 */
export const renderReviewJsonFile = (linkedData, id, field, theme) => {
  if (!Array.isArray(linkedData) || !id || !field) return null;

  const file = linkedData.find(
    (entry) =>
      entry.template === field &&
      entry.fileType === "JSON" &&
      typeof entry.s3Key === "string"
  );

  if (!file) return null;

  const fileKey = file.s3Key.replace(/^public\//, '');
  const fileName = `${id}.json`;

  return (
    <Box className="file-download-cell" sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%" }}>
      <FileDownloadActionButton
        title="Download JSON"
        theme={theme}
        fileType="JSON"
        onClick={() => downloadFile(fileKey, fileName)}
      />
    </Box>
  );
};

export const renderTextInputControl = ({ value, onBlur, onKeyDown, editable }) => {
  return (        
    <TextField
      variant="outlined"
      defaultValue={value}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      sx={{
        width: '600px', // Auto width
        minWidth: '100px', // Minimum width
        maxWidth: '600px', // Maximum width
        border: "none",
        "& .MuiOutlinedInput-notchedOutline": {
          border: "1px solid", // Added 'solid' to ensure the border style is applied
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          border: "2px solid",
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          border: "1px solid",
          boxShadow: "0 0 5px rgba(0, 0, 0, 0.2)", // Box shadow needs more specification
        },
        // Ensuring text aligns left in the input field itself
        ".MuiInputBase-input": {
          textAlign: "left",
        },
      }}
    />
  );
};

export const renderMultiLineTextField = ({ value, onBlur, attributes }) => {
  if (attributes?.rtf) {
    // Define rteRef within the function
    const rteRef = React.createRef();

    // Define the onBlur handler that uses rteRef
    const handleRTFBlur = () => {
      if (rteRef.current) {
        const editorContent = rteRef.current.editor.getHTML(); // or getText() if you need plain text
        onBlur(editorContent); // Pass the content to the provided onBlur handler
      } else {
        console.error("Editor ref is not available");
      }
    };

    return (
      <>
        <RichTextEditor
          ref={rteRef} // Attach the ref to the RichTextEditor
          extensions={[StarterKit, Superscript, Subscript]} // Tiptap extensions
          content={value} // Initial content for the editor
          onBlur={handleRTFBlur} // Use the custom onBlur handler
          renderControls={() => (
            <MenuControlsContainer>
              <MenuButtonBold />
              <MenuButtonItalic />
              <MenuButtonSuperscript />
              <MenuButtonSubscript />
            </MenuControlsContainer>
          )}
        />
        {attributes?.maxWords > 0 && (
          <div>
            {value?.count} words (<em>{attributes.maxWords} limit</em>)
          </div>
        )}
      </>
    );
  } else {
    return (
      <TextField
        variant="outlined"
        defaultValue={value}
        onBlur={onBlur}
        multiline
        rows={10}
        sx={{
          width: '80%',
          minWidth: '200px',
          maxWidth: '1200px',
          border: 'none',
          '& .MuiOutlinedInput-notchedOutline': {
            border: '1px solid',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            border: '2px solid',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            border: '1px solid',
            boxShadow: '0 0 5px rgba(0, 0, 0, 0.2)',
          },
          '.MuiInputBase-input': {
            textAlign: 'left',
          },
        }}
      />
    );
  }
};

export const render3stateBooleanForMetaPanel = ({ value, onChange, editable }) => {
  const val = formatValue(value);

  if (editable) {
    return (
      <Select
        value={val || ''}
        onChange={onChange}
        displayEmpty
        inputProps={{ "aria-label": "Without label" }}
        size="small"
        fullWidth
        sx={{
          width: 'auto',
          minWidth: '30px',
          maxWidth: '110px',
          border: "none",
          "& .MuiOutlinedInput-notchedOutline": {
            border: "1px solid",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            border: "2px solid",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            border: "1px solid",
            boxShadow: "0 0 5px rgba(0, 0, 0, 0.2)",
          },
          ".MuiSelect-select": {
            textAlign: "left",
          },
        }}
      >
        <MenuItem value="" />
        <MenuItem
          value="Yes"
          sx={{ display: 'flex', alignItems: 'center', textAlign: 'left' }}
        >
          <ListItemIcon>
            <CheckIcon />
          </ListItemIcon>
        </MenuItem>
        <MenuItem
          value="No"
          sx={{ display: 'flex', alignItems: 'center', textAlign: 'left' }}
        >
          <ListItemIcon>
            <CloseIcon />
          </ListItemIcon>
        </MenuItem>
      </Select>
    );
  } else {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <YesNoDisplay value={val} style={{ margin: '0 auto', display: 'block' }} />
      </Box>
    );
  }
};

export const render3stateBooleanForDetailPanel = ({ value, onChange, editable }) => {
 //   console.log("value", value);
 //   console.log("onChange", onChange);
 //   console.log("editable", editable);
  const val = formatValue( value );
   
  if(editable) {
    return (
      <Select
        value={val || ''}
        onChange={onChange}
        displayEmpty
        inputProps={{ "aria-label": "Without label" }}
        size="small"
        fullWidth
        sx={{
          width: 'auto', // Auto width
          minWidth: '30px', // Minimum width
          maxWidth: '110px', // Maximum width
          border: "none",
          "& .MuiOutlinedInput-notchedOutline": {
            border: "1px solid", // Added 'solid' to ensure the border style is applied
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            border: "2px solid",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            border: "1px solid",
            boxShadow: "0 0 5px rgba(0, 0, 0, 0.2)", // Box shadow needs more specification
          },
          // Ensuring text aligns left in the input field itself
          ".MuiSelect-select": {
            textAlign: "left",
          },
        }}
      >
      <MenuItem value="">
        {/* Display this when no option is selected */}
        {/* Blank option */}
      </MenuItem>
      <MenuItem
        value="Yes" // Changed to boolean true
        sx={{ display: 'flex', alignItems: 'center', textAlign: 'left' }}
      >
        <ListItemIcon>
          <CheckIcon />
        </ListItemIcon>
      </MenuItem>
      <MenuItem
        value="No" // Changed to boolean false
        sx={{ display: 'flex', alignItems: 'center', textAlign: 'left' }}
      >
        <ListItemIcon>
          <CloseIcon />
        </ListItemIcon>
      </MenuItem>
    </Select>
  );
} else {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
      }}
    >
      <YesNoDisplay value={val} style={{ margin: '0 auto', display: 'block' }} />
    </Box>
    )
  }
};

// Function to render Yes/No display
// Updated for meta context
export const render2StatePositiveBooleanForDetailPanel = ({ value, onChange, editable, context }) => {
  const val = formatValue(value);
  const contextClass =
    context === RenderContext.META_PANEL
      ? 'boolean-meta'
      : context === RenderContext.DETAIL_PANEL
      ? 'boolean-detail'
      : '';

  if (editable) {
    return (
      <Select
        value={val || ''}
        onChange={onChange}
        displayEmpty
        inputProps={{ "aria-label": "Yes/No select" }}
        size="small"
        fullWidth
        className={`boolean-select ${contextClass}`}
      >
        <MenuItem value="" />
        <MenuItem value="Yes" className="boolean-option">
          <ListItemIcon>
            <CheckIcon />
          </ListItemIcon>
        </MenuItem>
        <MenuItem value="No" className="boolean-option">
          <ListItemIcon>
            <CloseIcon />
          </ListItemIcon>
        </MenuItem>
      </Select>
    );
  }

  return (
    <Box className={`boolean-static ${contextClass}`}>
      <YesNoDisplay value={val} className="boolean-display" />
    </Box>
  );
};

export const renderDateCellForDetailPanel = ({ value, onChange, editable, context }) => {
  const dateValue =
    typeof value === "object" && value !== null
      ? value["$d"]
      : value;

  const initialValue =
    dateValue && dayjs(dateValue).isValid() ? dayjs(dateValue) : null;

    console.log("context", context);
  const contextClass = `render-datetime-${context.toLowerCase()}`;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DesktopDatePicker
        inputFormat="DD/MM/YYYY"
        value={initialValue}
        onChange={editable ? onChange : undefined}
        components={{
          textField: ({ inputRef, inputProps, InputProps }) => (
            <TextField
              {...inputProps}
              ref={inputRef}
              className={`date-picker-input ${contextClass}`}
              InputProps={{ ...InputProps }}
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              size="small"
              fullWidth
            />
          ),
        }}
      />
    </LocalizationProvider>
  );
};

export const renderDateTimeCellForDetailPanel = ({
  value,
  onChange,
  editable,
  context,
}) => {
  const dateValue =
    typeof value === "object" && value !== null ? value["$d"] : value;

  const initialValue =
    dateValue && dayjs(dateValue).isValid() ? dayjs(dateValue) : null;

  const contextClass = `render-datetime-${context?.toLowerCase()}`;

  if (!editable) {
    const displayValue =
      initialValue !== null
        ? initialValue.format("DD/MM/YYYY HH:mm") // ✅ 24-hour time format
        : "";

    return <span className={contextClass}>{displayValue}</span>;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DesktopDatePicker
        inputFormat="DD/MM/YYYY"
        value={initialValue}
        onChange={onChange}
        components={{
          textField: ({ inputRef, inputProps, InputProps }) => (
            <TextField
              {...inputProps}
              ref={inputRef}
              className={`date-picker-input ${contextClass}`}
              InputProps={{ ...InputProps }}
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              size="small"
              fullWidth
            />
          ),
        }}
      />
    </LocalizationProvider>
  );
};

// Updated for meta context
export const renderReadonlyText = (value, context) => {
  const displayValue = value !== null && value !== undefined ? value : "";

  if (context === RenderContext.DATAGRID) {
    return displayValue;
  }

  return (
    <span className={`render-value-${context.toLowerCase()}`}>
      {displayValue}
    </span>
  );
};

// options:
// {
//   resolveCountry?: (iso) => string,
//   showApproval?: boolean,
//   approvalField?: string,                 // default "approved"
//   rowId?: any,
//   onToggleApproval?: (rowId, authorIndex, nextBoolean) => void
// }
export const renderAuthors = (authors, allAffiliations, options = {}) => {
  const {
    showApproval = false,
    approvalField = "approved",
    onToggleApproval,
    rowId,
  } = options;

  if (!Array.isArray(authors) || authors.length === 0) {
    return <div style={{ padding: "8px 12px" }}>-</div>;
  }
  const items = authors.map((a, idx) => {
    const name = authorDisplayName(a);
    const star = a?.isPresenter ? "*" : "";
    const refs = parseAffiliationRefs(a?.affiliations);
    const affs = Array.isArray(allAffiliations)
      ? pickAffiliations(allAffiliations, refs, options)
      : [];
    const hint = affs.length ? ` (${affs[0]}${affs.length > 1 ? ` +${affs.length - 1}` : ""})` : "";

    const approved = isApprovedValue(a?.[approvalField]);
    const canToggle = showApproval && typeof onToggleApproval === "function";

    return (
      <span key={idx} style={{ display: "inline-flex", alignItems: "center", marginRight: 6 }}>
        <span title={affs.join("; ")}>{`${name}${star}${hint}`}</span>
        {showApproval ? (
          <button
            type="button"
            onClick={
              canToggle
                ? (e) => {
                    e.stopPropagation();
                    onToggleApproval(rowId, idx, !approved);
                  }
                : undefined
            }
            title={approved ? "Approved (click to unapprove)" : "Not approved (click to approve)"}
            style={{
              marginLeft: 6,
              border: "none",
              background: "transparent",
              cursor: canToggle ? "pointer" : "default",
              padding: 0,
              lineHeight: 1,
            }}
            disabled={!canToggle}
            aria-pressed={approved}
            aria-label={approved ? "Approved" : "Not approved"}
          >
            <span style={{ fontSize: 16 }}>{approved ? "✔︎" : "✘"}</span>
          </button>
        ) : null}
      </span>
    );
  });

  // Use TextField for consistent truncation behavior
  return (
    <TextField
      variant="outlined"
      value={""} // keep input empty; render the content overlay-style below
      inputProps={{
        style: {
          padding: "8px 12px",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          overflow: "hidden",
          readOnly: true,
        },
      }}
      sx={{
        width: "100%",
        border: "none",
        "& .MuiOutlinedInput-root": { padding: 0 },
        "& .MuiOutlinedInput-notchedOutline": { border: "none" },
        "&:hover .MuiOutlinedInput-notchedOutline": { border: "none" },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": { border: "none", boxShadow: "none" },
        ".MuiInputBase-input": { padding: 0, textAlign: "left" },
      }}
      fullWidth
      // Render authors as an overlayed content to allow buttons
      InputProps={{
        startAdornment: (
          <div
            style={{
              display: "inline-block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              width: "100%",
            }}
          >
            {items}
          </div>
        ),
      }}
    />
  );
};

export const renderAffiliations = (affiliations, options = {}) => {
  if (!Array.isArray(affiliations) || affiliations.length === 0) {
    return <div style={{ padding: "8px 12px" }}>-</div>;
  }
  const formatted = affiliations.map((a) => formatAffiliation(a, options)).filter(Boolean).join("; ");
  return (
    <TextField
      variant="outlined"
      value={formatted}
      inputProps={{
        style: {
          padding: "8px 12px",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          overflow: "hidden",
          readOnly: true,
        },
      }}
      sx={{
        width: "100%",
        border: "none",
        "& .MuiOutlinedInput-root": { padding: 0 },
        "& .MuiOutlinedInput-notchedOutline": { border: "none" },
        "&:hover .MuiOutlinedInput-notchedOutline": { border: "none" },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": { border: "none", boxShadow: "none" },
        ".MuiInputBase-input": { padding: 0, textAlign: "left" },
      }}
      fullWidth
    />
  );
};

// Detail panel ONLY. Uses existing module-level helpers; does not redefine them.
// options:
//   - approvalField?: string        // default "approved"
//   - rowId?: any
//   - onToggleApproval?: (rowId, authorIndex, nextBoolean) => void
//   - resolveCountry?: (iso) => string  // only if your formatAffiliation uses it via options
// Uses existing helpers: authorDisplayName, parseAffiliationRefs, formatAffiliation, isApprovedValue

export const renderAuthorsForDetailPanel = (authors, affiliations, options = {}) => {
  const approvalField = options.approvalField || "approved";
  const rowId = options.rowId;
  const onToggleApproval =
    typeof options.onToggleApproval === "function" ? options.onToggleApproval : null;

  if (!Array.isArray(authors)) return <div>No information available</div>;

  const safeAffiliations = Array.isArray(affiliations) ? affiliations : [];

  return (
    <div>
      {authors.map((a, idx) => {
        const name = authorDisplayName(a);
        const refs = parseAffiliationRefs(a?.affiliations);
        const affLines = Array.from(new Set(refs))
          .map((r) => safeAffiliations[r - 1] || null)
          .filter(Boolean)
          .map((aff) => formatAffiliation(aff, options))
          .filter(Boolean);

        const approved = isApprovedValue(a?.[approvalField]);

        return (
          <div key={idx} style={{ marginBottom: idx < authors.length - 1 ? 20 : 0 }}>
            <div><strong>Name:</strong> {name} {a?.isPresenter ? <em>(presenter)</em> : null}</div>
            {a?.title ? <div><strong>Title:</strong> {a.title}</div> : null}
            {a?.email ? <div><strong>Email:</strong> {a.email}</div> : null}

            <div>
              <strong>Affiliations:</strong>{" "}
              {affLines.length ? (
                <div>{affLines.map((line, i) => <div key={i}>{line}</div>)}</div>
              ) : (
                <span>None</span>
              )}
            </div>

            {/* Approval — last, with consistent line-height */}
            <div
              style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <strong>Approval:</strong>
              <span
                style={{
                  color: approved ? "green" : "red",
                  display: "inline-flex",
                  alignItems: "center",
                  lineHeight: 1, // prevent row height jump
                }}
                aria-label={approved ? "Approved" : "Not approved"}
                title={approved ? "Approved" : "Not approved"}
              >
                {approved ? "✔︎ Approved" : "✘ Not approved"}
              </span>

              <Switch
                size="small"
                checked={approved}
                color={approved ? "success" : "error"}
                inputProps={{ "aria-label": "Toggle approval" }}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => {
                  if (!onToggleApproval) return;
                  onToggleApproval(rowId, idx, e.target.checked);
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};



/*
export const renderAuthorsForDetailPanel = (authors, affiliations) => {
  console.log("Author&Affiliations");
  console.log("authors: ", authors);
  console.log("affiliations: ", affiliations);

  if (!Array.isArray(authors)) {
    return <div>No information available</div>;
  }

  return (
    <div>
      {authors.map((author, index) => (
        <div key={index} style={{ marginBottom: "20px" }}>
          <div>
            <strong>Given Name:</strong> {author.givenName}
          </div>
          <div>
            <strong>Family Name:</strong> {author.familyName}
          </div>
          <div>
            <strong>Affiliations:</strong>{" "}
            {Array.isArray(author.affiliations) && Array.isArray(affiliations) ? (
              author.affiliations.map((affiliationIndex) => {
                const affiliation = affiliations[affiliationIndex];
                return affiliation ? (
                  <div key={affiliationIndex}>
                    {affiliation.institution}, {affiliation.country}
                  </div>
                ) : (
                  <div key={affiliationIndex}>Affiliation data not available</div>
                );
              })
            ) : (
              <div>No affiliations available</div>
            )}
          </div>
          <div>
            <strong>Presenting:</strong> {author.presenting ? "Yes" : "No"}
          </div>
        </div>
      ))}
    </div>
  );
};
*/
// parse affiliation refs — handles two formats:
//   string "1,2" (legacy 1-based)   → [1, 2]
//   array  [0, 1] (0-based int)     → [1, 2]  (converted to 1-based for pickAffiliations)
const parseAffiliationRefs = (refs) => {
  if (Array.isArray(refs)) {
    return refs.map((n) => Number(n) + 1).filter((n) => Number.isFinite(n) && n > 0);
  }
  if (typeof refs !== "string" || !refs.trim()) return [];
  return refs
    .split(",")
    .map((s) => parseInt(s.trim(), 10))
    .filter((n) => Number.isFinite(n) && n > 0);
};

// resolve ISO → country name using a provided resolver (options.resolveCountry)
// fallback: return the code itself if unknown or resolver not provided
const resolveCountry = (code, options) => {
  if (!code) return "";
  const c = String(code).toUpperCase();
  if (options && typeof options.resolveCountry === "function") {
    try { const name = options.resolveCountry(c); if (name) return name; } catch (_) {}
  }
  return c; // fall back to ISO code
};

const formatAffiliation = (a, options) => {
  if (!a) return "";
  const parts = [];
  if (a.department && a.department.trim()) parts.push(a.department.trim());
  if (a.institution && a.institution.trim()) parts.push(a.institution.trim());
  if (a.state && a.state.trim()) parts.push(a.state.trim());
  if (a.country) parts.push(resolveCountry(a.country, options));
  return parts.join(", ");
};

// refs are 1-based; affiliations array is 0-based
const pickAffiliations = (all, refs, options) => {
  if (!Array.isArray(all) || !all.length || !refs.length) return [];
  const uniq = Array.from(new Set(refs));
  return uniq
    .map((r) => all[r - 1] ?? null)
    .filter(Boolean)
    .map((a) => formatAffiliation(a, options))
    .filter(Boolean);
};

const authorDisplayName = (a) =>
  [a?.givenName, a?.familyName].filter(Boolean).join(" ");

const isApprovedValue = (v) => {
  if (typeof v === "boolean") return v;
  if (v == null) return false;
  const s = String(v).trim().toLowerCase();
  return s === "1" || s === "true" || s === "yes" || s === "y" || s === "approved";
};

export const downloadFileForDetailPanel = ({ row, dataKey, projectCode, onFileUpdate }) => {
  let theFile;
  const acceptedFileTypes = ["*"];
  const maxFileCount = 1;

  // To store the result of processFile for later use
  let newFileResult;

  if (row && Array.isArray(row.files)) {
    theFile = row.files.find(file => file.dataKey === dataKey);
  } else {
    console.log("row or row.files is undefined or not an array.");
    return;
  }

  // Define the processFile function to generate the key for the file
  const processFile = async ({ file }) => {
    if (!file) {
      console.error('processFile received an invalid file:', file);
      return;
    }

    try {
      const fileExtension = file.name.split(".").pop();
      const filebuffer = await file.arrayBuffer();
      const hashBuffer = await window.crypto.subtle.digest("SHA-1", filebuffer);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashHex = hashArray.map((a) => a.toString(16).padStart(2, "0")).join("");

      const fileKey = `${hashHex}.${fileExtension}`;

      // Correct the creation of the new file object
      const newFile = {
        id: hashHex,
        dataKey: dataKey,
        fileKey: fileKey,
        hash: hashHex,
        uploaded: new Date().toISOString(),
        size: file.size,
        name: file.name,
      };

      console.log('Generated newFile object:', newFile);
      newFileResult = newFile;

      return { file, key: fileKey };
    } catch (error) {
      console.error('Error processing file:', error);
      throw new Error(`Error processing file: ${error.message}`);
    }
  };

  // Function to handle explicit file removal
  const handleFileRemoval = async () => {
    if (theFile) {
      console.log("Removing existing file:", theFile.fileKey);
      try {
        // Remove the file from AWS
        await removeFileFromAWS(theFile.fileKey);
        console.log(`File with key ${theFile.fileKey} removed from AWS`);

        // Remove the file from the submission by calling onFileUpdate with null
        onFileUpdate(row.id, null, dataKey);
      } catch (error) {
        console.error(`Error removing file from AWS: ${error}`);
        throw new Error(`Error removing file from AWS: ${error.message}`);
      }
    }
  };

  return (
    <div>
      {/* Render existing file */}
      {theFile && (
        <Box
          border={1}
          borderColor="grey.400"
          borderRadius="4px"
          padding="8px"
          display="flex"
          alignItems="center"
          mb={2}
        >
          <a href="#" onClick={() => {
              try {
                downloadFile(
                  theFile.fileKey,
                  `${projectCode}_${row.meta.trackingNumber || ""}_${dataKey}.${theFile.fileKey.split(".").pop()}`
                );
              } catch (error) {
                console.error('Error downloading file:', error);
              }
            }}
            style={{ textDecoration: "none", display: "flex", alignItems: "center" }}
          >
            <img
              src={getIconForFile(theFile.name)}
              alt="File Icon"
              style={{ marginRight: "8px", height: "30px", cursor: "pointer" }}
            />
            <Text style={{ cursor: "pointer", color: "blue" }}>{theFile.name}</Text>
          </a>
          <AmplifyButton
            onClick={async () => {
              try {
                await handleFileRemoval();
              } catch (error) {
                console.error('Error handling file removal:', error);
              }
            }}
            style={{ backgroundColor: "#1976d2", color: 'white', marginLeft: "auto", fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif"', fontWeight: "500", fontSize: "0.875rem", lineHeight: "1.75", letterSpacing: "0.02857em", textTransform: "uppercase", height: "35px" }}
          >
            Remove
          </AmplifyButton>
        </Box>
      )}

      {/* File upload manager */}
      <StorageManager
        id={dataKey}
        acceptedFileTypes={acceptedFileTypes}
        path="public/"
        maxFileCount={maxFileCount}
        processFile={processFile}
        isResumable
        onUploadStart={async ({ key }) => {
          // No operations needed here, as per your direction
        }}
        onUploadSuccess={({ key }) => {
          try {
            console.log('File uploaded successfully:', key);
            if (newFileResult) {
              console.log('Received newFile object from processFile:', newFileResult);
              onFileUpdate(row.id, newFileResult, dataKey);
            } else {
              console.error('Error: newFile not found');
            }
          } catch (error) {
            console.error('Error in onUploadSuccess:', error);
          }
        }}
        onUploadError={(error, { key }) => {
          console.error("Error uploading file:", error, key);
        }}
        components={{
          FilePicker({ onClick }) {
            return (
              <Button
                style={{ backgroundColor: "#1976d2", color: 'white' }}
                onClick={onClick}
              >
                Browse Files
              </Button>
            );
          },
        }}
      />
    </div>
  );
};

export const removeFileFromAWS = async (fileKey) => {
  try {
    if (!fileKey || typeof fileKey !== 'string') {
      throw new Error(`Invalid fileKey: ${fileKey}`);
    }

    // Ensure "public/" is prepended to the file key
    const fullFileKey = `public/${fileKey}`;

    // Use remove with the correct key
    const removedObject = await remove({ path: fullFileKey });
    console.log(`Deleted file from AWS: ${removedObject}`);
  } catch (error) {
    console.error(`Error removing file ${fileKey} from AWS: `, error);
  }
};

// Helper function for getting the file icon based on the file extension
const getIconForFile = (fileName) => {
  const extension = fileName.split(".").pop().toLowerCase();
  const extensionToIconMap = {
    xlsx: excelIcon,
    xls: excelIcon,
    csv: csvIcon,
    doc: wordIcon,
    docx: wordIcon,
    pdf: pdfIcon,
    png: imageIcon,
    jpg: imageIcon,
    jpeg: imageIcon,
    gif: imageIcon,
  };
  return extensionToIconMap[extension] || documentIcon;
};

export const formatValue = ( value ) => {
  let val;

  if (value === null) {
    val = "";
  } else {
    val = value;

    if (typeof value === "undefined") {
      val = "";
    }
    if (value === false) {
      val = "No";
    }
    if (value === true) {
      val = "Yes";
    }
  }

  return val;
};

// 1.2) helper to parse ">80", "=high", etc.
function meetsThreshold(value, expr) {
  const m = expr.match(/^([<>=])\s*(.+)$/);
  if (!m) return false;
  const [ , op, raw ] = m;
  if (typeof value === "number") {
    const num = parseFloat(raw);
    if (op === ">")  return value >  num;
    if (op === "<")  return value <  num;
    if (op === "=")  return value === num;
  } else {
    if (op === "=") return String(value) === raw;
  }
  return false;
}

function extractFirstTextFromHtml(html) {
  if (!html || typeof html !== "string") return "";

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    // Prefer: first <h4> + first <p> after it
    const h4 = doc.querySelector("h4");
    if (h4) {
      const p = h4.nextElementSibling && h4.nextElementSibling.tagName === "P"
        ? h4.nextElementSibling
        : doc.querySelector("p");

      const h4Text = (h4.textContent || "").trim();
      const pText = (p?.textContent || "").trim();

      const combined = [h4Text, pText].filter(Boolean).join(" — ");
      return combined;
    }

    // Fallback: first <p>
    const p = doc.querySelector("p");
    if (p) return (p.textContent || "").trim();

    // Fallback: any text
    return (doc.body?.textContent || "").trim();
  } catch (_) {
    // Very defensive fallback if DOMParser ever fails
    const stripped = html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
    return stripped;
  }
}

function truncateText(s, max = 220) {
  const str = (s || "").trim();
  if (!str) return "";
  if (str.length <= max) return str;
  return `${str.slice(0, max - 1).trimEnd()}…`;
}


export function renderAiReviewCellPanel({ value, fieldConfig, row, additionalProps, theme }) {
  const isEmptyObject = (v) =>
    v != null && typeof v === "object" && !Array.isArray(v) && Object.keys(v).length === 0;

  if (value == null || isEmptyObject(value)) return null;

  const scoreKey = fieldConfig?.details?.quality?.dataSource;
  const confidenceKey = fieldConfig?.details?.confidence?.dataSource;

  const score = scoreKey ? value?.[scoreKey] : undefined;
  const confidence = confidenceKey ? value?.[confidenceKey] : undefined;

  const handleClick = (event) => {
    event.stopPropagation();

    additionalProps?.onOpenAiReviewPanel?.({
      row,
      value,
      fieldConfig,
      anchorEl: event.currentTarget,
    });
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleClick(event);
    }
  };

  return (
    <div
      style={{ display: "flex", gap: 6, alignItems: "center" }}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
    >
      <AIReviewSphere
        score={score}
        confidence={confidence}
        theme={theme}
        size={46}
        styleVariant="attention"
      />
    </div>
  );
}
